import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { useGetFriendsNumbers, useGetMyNumbers, useGetMyNumbersData } from '@/api';
import { MyNumber } from '@/api/types';
import { Filter } from '@/components/AllNumbers/Filter/Filter.tsx';
import { ArrowDownIcon } from '@/components/icons/main-block';
import { CalendarIcon } from '@/components/icons/my-numbers';
import { sortOptions } from '@/components/MyNumbers/constants.ts';
import { FriendsList } from '@/components/MyNumbers/FriendsList/FriendsList.tsx';
import { InfoBlock } from '@/components/MyNumbers/InfoBlock/InfoBlock.tsx';
import { MyNumbersList } from '@/components/MyNumbers/MyNumbersList/MyNumbersList.tsx';
import { Dropdown } from '@/components/shared/Dropdown/Dropdown.tsx';
import { DropdownOption, SelectOnChange } from '@/types';
import { formatMonthYear, getCurrentYearMonth, getDateOptions } from '@/utils';

import cn from './styles.module.sass';

export const MyNumbers = () => {
    const currentDate = useMemo(() => {
        return getCurrentYearMonth();
    }, []);

    const [isMyNumbers, setIsMyNumbers] = useState(true);
    const [myNumsSelectedDate, setMyNumsSelectedDate] = useState<DropdownOption<string>>(() => ({
        value: currentDate,
        label: formatMonthYear(new Date(currentDate)),
    }));
    const [friendsSelectedDate, setFriendsSelectedDate] = useState<DropdownOption<string>>(() => ({
        value: currentDate,
        label: formatMonthYear(new Date(currentDate)),
    }));
    const [myNums, setMyNums] = useState<MyNumber[]>([]);
    const [currentSort, setCurrentSort] = useState<MyNumber['status'] | 'all'>('all');

    const { data: myNumsStartDateData, isLoading: isMyNumsStartDateDataLoading } =
        useGetMyNumbersData();
    const { data: myNumsData, isLoading: isMyNumsDataLoading } = useGetMyNumbers(
        myNumsSelectedDate.value
    );
    const { data: friendsData, isLoading: isFriendsDataLoading } = useGetFriendsNumbers(
        friendsSelectedDate.value
    );

    const dates = useMemo(() => {
        const currentDate = getCurrentYearMonth();
        const startPeriod = isMyNumbers
            ? myNumsStartDateData?.data.ownStartPeriod
            : myNumsStartDateData?.data.friendsStartPeriod;

        return getDateOptions(startPeriod ?? currentDate, currentDate);
    }, [
        isMyNumbers,
        myNumsStartDateData?.data.friendsStartPeriod,
        myNumsStartDateData?.data.ownStartPeriod,
    ]);

    const handleDateChange = (newValue: unknown) => {
        const value = newValue as DropdownOption<string>;

        if (isMyNumbers) {
            setMyNumsSelectedDate(value);
        } else {
            setFriendsSelectedDate(value);
        }
    };

    const handleSortChange = (newValue: DropdownOption<MyNumber['status'] | 'all'>) => {
        setCurrentSort(newValue.value);
    };

    const handleSwitch = (isMyNums: boolean) => {
        setIsMyNumbers(isMyNums);
        setCurrentSort('all');
    };

    useEffect(() => {
        if (myNumsData?.data) {
            setMyNums(myNumsData.data);

            if (currentSort === 'all') {
                setMyNums(myNumsData.data);
            } else {
                const filteredMyNums = myNumsData.data.filter(
                    ({ status }) => status === currentSort
                );
                setMyNums(filteredMyNums);
            }
        }
    }, [myNumsData?.data, currentSort]);

    return (
        <div className={cn.container}>
            <h2 className={cn.pageName}>Мои номера</h2>
            <div className={clsx(cn.statuses, !isMyNumbers && cn.friendsSales)}>
                <div className={cn.statusesContainer}>
                    <InfoBlock status="active" />
                    <InfoBlock status="inactive" />
                    <InfoBlock status="delivery" />
                </div>

                <div className={cn.friendsSalesInfo}>
                    <div className={cn.drugomaniyaImg}>
                        <img src="/images/icons/operators/drugomaniya.svg" alt="" />
                    </div>
                    <h4>Продажи твоих друзей</h4>
                    <p>
                        Сумма твоего дохода от продаж каждого приглашенного друга:
                        <br />
                        20 Б за каждый активный номер ежемесячно
                    </p>
                </div>
            </div>
            <div className={cn.switch}>
                <div
                    onClick={() => setIsMyNumbers(true)}
                    className={clsx(cn.switchItem, isMyNumbers && cn.active)}
                >
                    Мои номера
                </div>
                <div
                    onClick={() => handleSwitch(false)}
                    className={clsx(cn.switchItem, !isMyNumbers && cn.active)}
                >
                    Продажи друзей
                </div>
            </div>
            <div className={cn.numbersList}>
                <div className={cn.toolbar}>
                    {isMyNumsStartDateDataLoading ? (
                        <Skeleton height={16} width={150} />
                    ) : (
                        <div className={cn.datesContainer}>
                            <div className={cn.calendarIcon}>{CalendarIcon}</div>
                            <div className={cn.arrowDownIcon}>{ArrowDownIcon}</div>
                            <Dropdown
                                options={dates}
                                defaultValue={dates[0]}
                                value={isMyNumbers ? myNumsSelectedDate : friendsSelectedDate}
                                onChange={handleDateChange}
                                classNames={{
                                    control: () => cn.datesControl,
                                    placeholder: () => cn.datesPlaceholder,
                                    singleValue: () => cn.datesSingleValue,
                                    menu: () => cn.datesMenu,
                                }}
                                components={{ DropdownIndicator: null }}
                                placeholder="Выбрать дату"
                            />
                        </div>
                    )}

                    {isMyNumbers && (
                        <Filter
                            options={sortOptions}
                            placeholder="Сортировать"
                            onChange={handleSortChange as SelectOnChange}
                            classNames={{
                                menu: () => cn.filterMenu,
                            }}
                        />
                    )}
                </div>
                {isMyNumbers ? (
                    <MyNumbersList
                        isLoading={isMyNumsDataLoading || isMyNumsStartDateDataLoading}
                        numbers={myNums}
                    />
                ) : (
                    <FriendsList
                        isLoading={isFriendsDataLoading || isMyNumsStartDateDataLoading}
                        friends={friendsData ?? []}
                    />
                )}
            </div>
        </div>
    );
};
